import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2, MailWarning } from 'lucide-react';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { notify, getApiErrorMessage } from '@/lib/toast';

/**
 * POS-I185: shown to the owner while the sign-up e-mail is unverified. Days left and the lock come from
 * the server (auth/me tenant.emailVerification), never from the browser clock. Resend asks the server for
 * a new link (at most 3 an hour).
 */
export function EmailVerifyBanner() {
  const { t } = useTranslation('auth');
  const state = useAuthStore((s) => s.tenant?.emailVerification);
  const email = useAuthStore((s) => s.user?.email ?? '');
  const refreshPermissions = useAuthStore((s) => s.refreshPermissions);
  const [pending, setPending] = useState(false);

  if (!state?.required || state.verified) return null;
  const locked = state.locked;
  const daysLeft = state.daysLeft ?? 0;

  const resend = async () => {
    if (pending) return;
    setPending(true);
    try {
      const res = await api.post<{ sent: boolean; alreadyVerified: boolean }>('/auth/resend-verification');
      if (res.data.alreadyVerified) {
        notify.info(t('verifyResendAlready'));
        void refreshPermissions();
      } else {
        notify.info(t('verifyResendSent'));
      }
    } catch (err) {
      notify.error(getApiErrorMessage(err, t('verifyResendFailed')));
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      role="status"
      data-testid="email-verify-banner"
      className={
        locked
          ? 'sticky top-0 z-30 print:hidden flex flex-wrap items-center gap-x-3 gap-y-2 bg-destructive px-4 py-3 text-sm font-medium text-destructive-foreground shadow-lg'
          : 'print:hidden flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-warning/40 bg-warning px-4 py-2.5 text-sm font-medium text-warning-foreground'
      }
    >
      <MailWarning className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-[14rem] flex-1">
        <strong>{locked ? t('verifyBannerLockedTitle') : t('verifyBannerTitle')}</strong>
        {' — '}
        {locked
          ? t('verifyBannerLockedBody', { email })
          : daysLeft <= 0
            ? t('verifyBannerBodyToday', { email })
            : t('verifyBannerBody', { count: daysLeft, email })}
      </span>
      <button
        type="button"
        onClick={resend}
        disabled={pending}
        className="shrink-0 inline-flex items-center gap-1.5 max-md:min-h-11 rounded-md bg-background/20 px-3 py-2 text-xs font-semibold hover:bg-background/30 transition-colors disabled:opacity-60"
      >
        {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
        {t('verifyResend')}
      </button>
      <Link to="/admin/users" className="shrink-0 text-xs font-semibold underline underline-offset-2">
        {t('verifyChangeAddress')}
      </Link>
    </div>
  );
}
